/*
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import qs from 'qs';
import useSWR from 'swr';

import request from '@/utils/request';
import type * as Type from '@/common/interface';

export const useQuestionSearch = (params: Type.AdminContentsReq) => {
  const apiUrl = `/answer/admin/api/question/page?${qs.stringify(params)}`;
  const { data, error, mutate } = useSWR<Type.ListResult, Error>(
    [apiUrl],
    request.instance.get,
  );
  return {
    data,
    isLoading: !data && !error,
    error,
    mutate,
  };
};

export const changeQuestionStatus = (
  question_id: string,
  status: Type.AdminQuestionStatus,
) => {
  return request.put('/answer/admin/api/question/status', {
    question_id,
    status,
  });
};

export const getQuestionSetting = () => {
  return request.get<Type.AdminQuestionSetting>(
    '/answer/admin/api/siteinfo/question',
  );
};

export const updateQuestionSetting = (params: Type.AdminQuestionSetting) => {
  return request.put('/answer/admin/api/siteinfo/question', params);
};

export const useAdminContentCategories = () => {
  const { data, error, mutate } = useSWR<Type.ContentCategoryItem[]>(
    '/answer/admin/api/content-categories',
    request.instance.get,
  );
  return { data: data || [], isLoading: !data && !error, error, mutate };
};

export const createContentCategory = (
  params: Partial<Type.ContentCategoryItem>,
) => request.post('/answer/admin/api/content-categories', params);

export const updateContentCategory = (
  params: Partial<Type.ContentCategoryItem>,
) => request.put('/answer/admin/api/content-categories', params);

export const assignQuestionCategory = (
  question_id: string,
  category_id: number,
) =>
  request.put('/answer/admin/api/question/category', {
    question_id,
    category_id,
  });

export const pinQuestionUntil = (question_id: string, pin_until: number) =>
  request.put('/answer/admin/api/question/pin', { question_id, pin_until });

export const unpinQuestion = (question_id: string) =>
  request.delete('/answer/admin/api/question/pin', { question_id });
